import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import { Calendar } from "@/components/calendar";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/dialog";
import { Editor } from "@/components/editor";
import { Input } from "@/components/input";
import { Label } from "@/components/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/select";
import { useSetExperimentRollout, useUpdateExperiment } from "@/http/api-hooks";
import {
  Experiment,
  ExperimentCampaign,
  ExperimentOutcome,
  ExperimentRolloutStatus,
  ExperimentType,
  UpdateExperimentRequest,
} from "@/http/api-types";
import { cn, formatUTCDate, formatUTCDateTime, getErrorMessage } from "@/lib/utils";
import { format } from "date-fns";
import { CalendarIcon, CheckCircle, TimerReset } from "lucide-react";
import { useEffect, useState, type Dispatch } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { toast } from "sonner";
import { parseTimeFromDate, type ExperimentFormValues } from "../ExperimentForm";
import { RolloutDurabilityDialog } from "../RolloutDurabilityDialog";
import { combineDateAndTime, toUTCCalendarDate } from "../experiment-form-helpers";
import { EXPERIMENT_OUTCOME_OPTIONS, getExperimentOutcomeOption } from "../experiment-outcome";
import {
  canRecordRolloutDurability,
  EXPERIMENT_ROLLOUT_OPTIONS,
  formatRolloutDurabilityAge,
  getExperimentRolloutDurabilityFreshness,
  getExperimentRolloutDurabilityOption,
  getExperimentRolloutOption,
  getRolloutDurabilityFreshnessBadgeVariant,
} from "../experiment-rollout";
import { getExperimentStatus, getExperimentStatusLabel, getExperimentStatusVariant } from "../experiment-status";
import { formatLearningLoopDuration, getExperimentLearningLoopClosure } from "./summary-tab";

const rollout_additional_info_PLACEHOLDER =
  "Link to a pull request, production flight/campaign change, or Confluence page with documented learning.";

type SummaryTabProps = {
  experiment: Experiment;
  isEditMode?: boolean;
  initialCampaigns?: ExperimentCampaign[];
  onSaveSuccess: Dispatch<Experiment | undefined>;
  onCancel: () => void;
};

function buildSummaryUpdatePayload(
  experiment: Experiment,
  hypothesis: string,
  conclusion: string,
  rollOutAdditionalInfo: string,
  campaigns: ExperimentCampaign[],
  outcome?: ExperimentOutcome,
): UpdateExperimentRequest {
  const payload: UpdateExperimentRequest = {
    name: experiment.name,
    type: experiment.type,
    hypothesis,
    owner: experiment.owner,
    start_date: experiment.start_date,
    end_date: experiment.end_date,
    external_links: experiment.external_links || [],
    conclusion: conclusion.trim() || undefined,
    outcome: outcome ?? experiment.outcome,
    rollout_additional_info: rollOutAdditionalInfo.trim() || undefined,
  };

  if (experiment.type === ExperimentType.Custom) {
    payload.organizations = experiment.organizations;
    payload.audiences = experiment.audiences;
    payload.campaigns = campaigns.map(c => ({
      campaign_id: c.campaign_id,
      control_campaign_id: c.control_campaign_id,
      test_group: c.test_group,
      variant: c.variant,
      traffic_allocation: c.traffic_allocation,
      params: c.params?.map(p => ({
        param_name: p.param_name,
        param_value: p.param_value,
      })),
    }));
  } else {
    payload.imported_global_experiment_id = experiment.imported_global_experiment_id;
    payload.import_last_synced_at = experiment.import_last_synced_at;
    payload.scope_filters = experiment.scope_filters;
    payload.import_metadata = experiment.import_metadata;
    payload.prediction_flights =
      experiment.prediction_flights?.map(pf => ({
        prediction_flight_id: pf.prediction_flight_id,
        variant: pf.variant,
        traffic_allocation: pf.traffic_allocation,
        params: pf.params?.map(p => ({
          predictor_type_id: p.predictor_type_id,
          param_value: p.param_value,
        })),
      })) || [];
  }

  return payload;
}

function SideRailMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2 text-sm">
      <span className="min-w-0 text-muted-foreground">{label}</span>
      <span className="text-right font-mono tabular-nums">{value}</span>
    </div>
  );
}

export function SummaryTab({
  experiment,
  isEditMode = false,
  initialCampaigns,
  onSaveSuccess,
  onCancel,
}: SummaryTabProps) {
  const status = getExperimentStatus(experiment);
  const statusLabel = getExperimentStatusLabel(status);
  const statusVariant = getExperimentStatusVariant(status);
  const showConcludeButton = !isEditMode && (status === "active" || status === "waiting_for_outcome");
  const currentOutcomeOption = getExperimentOutcomeOption(experiment.outcome);
  const currentRolloutOption = getExperimentRolloutOption(experiment.rollout_status);
  const hasRollout = Boolean(experiment.rollout_status || experiment.rollout_at);
  const canSetRollout = status === "concluded" && !hasRollout;
  const learningLoopClosure = getExperimentLearningLoopClosure(experiment);
  const [isConcludeFlowActive, setIsConcludeFlowActive] = useState(false);
  const [conclusionDraft, setConclusionDraft] = useState(experiment.conclusion || "");
  const [isConcludeDialogOpen, setIsConcludeDialogOpen] = useState(false);
  const [selectedOutcome, setSelectedOutcome] = useState<ExperimentOutcome | "">(experiment.outcome || "");
  const [selectedRolloutStatus, setSelectedRolloutStatus] = useState<ExperimentRolloutStatus | "">(
    experiment.rollout_status || "",
  );
  const [rolloutDate, setRolloutDate] = useState<Date | null>(toUTCCalendarDate(experiment.rollout_at));
  const [rolloutTime, setRolloutTime] = useState(
    experiment.rollout_at ? parseTimeFromDate(new Date(experiment.rollout_at)) : "00:00",
  );
  const [rolloutPopoverOpen, setRolloutPopoverOpen] = useState(false);
  const [isRolloutDialogOpen, setIsRolloutDialogOpen] = useState(false);
  const [rollOutAdditionalInfoDraft, setRolloutAdditionalInfoDraft] = useState(
    experiment.rollout_additional_info || "",
  );
  const currentDurabilityOption = getExperimentRolloutDurabilityOption(experiment.rollout_durability_status);
  // Recomputed per render rather than memoised: it is a cheap date subtraction, and
  // pinning it would leave the badge stale after a confirmation lands.
  const durabilityFreshness = getExperimentRolloutDurabilityFreshness(experiment, new Date());
  const canSetDurability = canRecordRolloutDurability(experiment);
  const [isDurabilityDialogOpen, setIsDurabilityDialogOpen] = useState(false);
  const { trigger: updateExperiment, isMutating: isSaving } = useUpdateExperiment(experiment.id);
  const { trigger: setExperimentRollout, isMutating: isSettingRollout } = useSetExperimentRollout(experiment.id);
  const {
    control,
    getValues,
    setValue,
    trigger,
    formState: { errors, isSubmitting, isDirty },
  } = useFormContext<ExperimentFormValues>();

  const campaigns = initialCampaigns ?? [];
  const hasConclusionContent = conclusionDraft.trim().length > 0;

  useEffect(() => {
    setIsConcludeFlowActive(false);
    setConclusionDraft(experiment.conclusion || "");
    setSelectedOutcome(experiment.outcome || "");
    setIsConcludeDialogOpen(false);
    setSelectedRolloutStatus(experiment.rollout_status || "");
    setRolloutDate(toUTCCalendarDate(experiment.rollout_at));
    setRolloutTime(experiment.rollout_at ? parseTimeFromDate(new Date(experiment.rollout_at)) : "00:00");
    setRolloutPopoverOpen(false);
    setIsRolloutDialogOpen(false);
    setRolloutAdditionalInfoDraft(experiment.rollout_additional_info || "");
    setValue("rollOutAdditionalInfo", experiment.rollout_additional_info || "", { shouldDirty: false });
    // The durability form re-seeds itself when it opens; only its visibility is ours.
    setIsDurabilityDialogOpen(false);
  }, [
    experiment.id,
    experiment.conclusion,
    experiment.outcome,
    experiment.rollout_additional_info,
    experiment.rollout_at,
    experiment.rollout_status,
    setValue,
  ]);

  const handleSummarySave = async () => {
    const isValid = await trigger("hypothesis");
    if (!isValid) {
      toast.error(errors.hypothesis?.message || "Please fix the form errors");
      return;
    }

    const hypothesis = (getValues("hypothesis") || "").trim();
    const conclusion = getValues("conclusion") || "";
    const rollOutAdditionalInfo = getValues("rollOutAdditionalInfo") || "";
    const outcome = (getValues("outcome") || experiment.outcome) as ExperimentOutcome | undefined;

    try {
      const payload = buildSummaryUpdatePayload(
        experiment,
        hypothesis,
        conclusion,
        rollOutAdditionalInfo,
        campaigns,
        outcome,
      );
      const updatedExperiment = await updateExperiment(payload);
      toast.success("Summary updated successfully");
      onSaveSuccess(updatedExperiment);
    } catch (error) {
      toast.error("Failed to update summary", { description: getErrorMessage(error) });
    }
  };

  const handleSummaryCancel = () => {
    setValue("hypothesis", experiment.hypothesis || "", { shouldDirty: false });
    setValue("conclusion", experiment.conclusion || "", { shouldDirty: false });
    setValue("outcome", experiment.outcome || "", { shouldDirty: false });
    setValue("rollOutAdditionalInfo", experiment.rollout_additional_info || "", { shouldDirty: false });
    onCancel();
  };

  const handleConcludeExperiment = async () => {
    if (!selectedOutcome) {
      toast.error("Please select an outcome before concluding");
      return;
    }

    const hypothesis = experiment.hypothesis || "";
    const conclusion = conclusionDraft.trim();
    if (!conclusion.trim()) {
      toast.error("Please add a conclusion before concluding the experiment");
      return;
    }

    try {
      const payload = buildSummaryUpdatePayload(
        experiment,
        hypothesis,
        conclusion,
        experiment.rollout_additional_info || "",
        campaigns,
        selectedOutcome,
      );
      const updatedExperiment = await updateExperiment(payload);
      setValue("conclusion", conclusion, { shouldDirty: false });
      setValue("outcome", selectedOutcome, { shouldDirty: false });
      setIsConcludeFlowActive(false);
      setIsConcludeDialogOpen(false);
      toast.success("Experiment concluded successfully");
      onSaveSuccess(updatedExperiment);
    } catch (error) {
      toast.error("Failed to conclude experiment", { description: getErrorMessage(error) });
    }
  };

  const resetRolloutDraft = () => {
    setSelectedRolloutStatus(experiment.rollout_status || "");
    setRolloutDate(toUTCCalendarDate(experiment.rollout_at));
    setRolloutTime(experiment.rollout_at ? parseTimeFromDate(new Date(experiment.rollout_at)) : "00:00");
    setRolloutPopoverOpen(false);
    setRolloutAdditionalInfoDraft(experiment.rollout_additional_info || "");
  };

  const handleOpenRolloutDialog = () => {
    resetRolloutDraft();
    setIsRolloutDialogOpen(true);
  };

  const handleRolloutDialogOpenChange = (open: boolean) => {
    if (isSettingRollout) {
      return;
    }
    setIsRolloutDialogOpen(open);
    if (!open) {
      resetRolloutDraft();
    }
  };

  const handleSetRollout = async () => {
    if (!selectedRolloutStatus) {
      toast.error("Please select a rollout status");
      return;
    }
    if (!rolloutDate) {
      toast.error("Please select a rollout timestamp");
      return;
    }
    const rollOutAdditionalInfo = rollOutAdditionalInfoDraft.trim();
    if (!rollOutAdditionalInfo) {
      toast.error("Please add rollout additional info before setting rollout");
      return;
    }

    try {
      const rolloutAt = combineDateAndTime(rolloutDate, rolloutTime, false);
      const updatedExperiment = await setExperimentRollout({
        rollout_status: selectedRolloutStatus,
        rollout_at: rolloutAt.toISOString(),
        rollout_additional_info: rollOutAdditionalInfo,
      });
      setValue("rollOutAdditionalInfo", rollOutAdditionalInfo, { shouldDirty: false });
      setRolloutAdditionalInfoDraft(rollOutAdditionalInfo);
      setIsRolloutDialogOpen(false);
      toast.success("Rollout updated successfully");
      onSaveSuccess(updatedExperiment);
    } catch (error) {
      toast.error("Failed to set rollout", { description: getErrorMessage(error) });
    }
  };

  const handleOpenDurabilityDialog = () => {
    setIsDurabilityDialogOpen(true);
  };

  const isSavingSummary = isSaving || isSubmitting;
  const saveDisabled = isSavingSummary || !isDirty;
  const hasRolloutAdditionalInfoDraft = rollOutAdditionalInfoDraft.trim().length > 0;

  return (
    <div className="space-y-12">
      <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_260px]">
        {/* Narrative column */}
        <div className="space-y-10 min-w-0">
          {/* Hypothesis */}
          <section>
            <header className="mb-4 flex items-baseline justify-between border-b border-border pb-2">
              <h3 className="text-lg font-semibold tracking-tight">Hypothesis</h3>
            </header>
            {isEditMode ? (
              <Controller
                control={control}
                name="hypothesis"
                rules={{
                  validate: value => value.trim().length > 0 || "Hypothesis is required",
                }}
                render={({ field }) => (
                  <>
                    <Editor
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Type / for commands"
                      experimentId={experiment.id?.toString()}
                    />
                    {errors.hypothesis && <p className="text-sm text-destructive mt-1">{errors.hypothesis.message}</p>}
                  </>
                )}
              />
            ) : experiment.hypothesis ? (
              <Editor value={experiment.hypothesis} readOnly experimentId={experiment.id?.toString()} />
            ) : (
              <p className="text-sm text-muted-foreground">No hypothesis provided.</p>
            )}
          </section>

          {/* Conclusion */}
          <section>
            <header className="mb-4 flex items-baseline justify-between gap-3 border-b border-border pb-2">
              <h3 className="text-lg font-semibold tracking-tight">Conclusion</h3>
              {experiment.concluded_at && (
                <span className="text-xs text-muted-foreground">{formatUTCDateTime(experiment.concluded_at)}</span>
              )}
            </header>
            {isEditMode ? (
              <Controller
                control={control}
                name="conclusion"
                render={({ field }) => (
                  <Editor
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Add a conclusion..."
                    experimentId={experiment.id?.toString()}
                  />
                )}
              />
            ) : (
              <div className="space-y-4">
                {currentOutcomeOption && (
                  <div className="rounded-md border border-border bg-muted/20 p-3">
                    <p className="text-sm font-medium">{currentOutcomeOption.label}</p>
                    <p className="text-sm text-muted-foreground mt-1">{currentOutcomeOption.description}</p>
                  </div>
                )}
                {isConcludeFlowActive ? (
                  <Editor
                    value={conclusionDraft}
                    onChange={setConclusionDraft}
                    placeholder="Add a conclusion..."
                    experimentId={experiment.id?.toString()}
                  />
                ) : experiment.conclusion ? (
                  <Editor value={experiment.conclusion} readOnly experimentId={experiment.id?.toString()} />
                ) : !showConcludeButton ? (
                  <p className="text-sm text-muted-foreground">No conclusion recorded.</p>
                ) : null}
                {showConcludeButton &&
                  (isConcludeFlowActive ? (
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => {
                          setSelectedOutcome("");
                          setIsConcludeDialogOpen(true);
                        }}
                        disabled={isSaving || !hasConclusionContent}
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Select Outcome
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setIsConcludeFlowActive(false);
                          setConclusionDraft(experiment.conclusion || "");
                        }}
                        disabled={isSaving}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <Button
                      onClick={() => {
                        setIsConcludeFlowActive(true);
                        setConclusionDraft(experiment.conclusion || "");
                      }}
                      disabled={isSaving}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Conclude Experiment
                    </Button>
                  ))}
                {showConcludeButton && isConcludeFlowActive && !hasConclusionContent && (
                  <p className="text-sm text-muted-foreground">Add a conclusion before selecting an outcome.</p>
                )}
              </div>
            )}
          </section>

          {/* Rollout */}
          <section>
            <header className="mb-4 flex items-baseline justify-between gap-3 border-b border-border pb-2">
              <h3 className="text-lg font-semibold tracking-tight">Rollout</h3>
              {experiment.rollout_at && (
                <span className="text-xs text-muted-foreground">{formatUTCDateTime(experiment.rollout_at)}</span>
              )}
            </header>
            <div className="space-y-4">
              {currentRolloutOption && (
                <div className="rounded-md border border-border bg-muted/20 p-3">
                  <p className="text-sm font-medium">{currentRolloutOption.label}</p>
                  <p className="text-sm text-muted-foreground mt-1">{currentRolloutOption.description}</p>
                </div>
              )}
              {experiment.rollout_additional_info ? (
                <p className="whitespace-pre-wrap break-words text-sm leading-6">
                  {experiment.rollout_additional_info}
                </p>
              ) : !currentRolloutOption ? (
                <p className="text-sm text-muted-foreground">
                  {canSetRollout ? "No rollout decision recorded." : "Available after conclusion."}
                </p>
              ) : null}
              {canSetRollout && !isEditMode && (
                <Button onClick={handleOpenRolloutDialog} disabled={isSettingRollout}>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Set Rollout
                </Button>
              )}
            </div>
          </section>

          {/* Rollout durability — only meaningful once a change has actually shipped. */}
          {canSetDurability && (
            <section>
              <header className="mb-4 flex items-baseline justify-between gap-3 border-b border-border pb-2">
                <h3 className="text-lg font-semibold tracking-tight">Rollout Durability</h3>
                {/* Only a revert or a churn has a date worth heading the section with.
                    A still-live rollout's ended_at is the write instant, which the
                    "Recorded" line below already says. */}
                {currentDurabilityOption?.endedAtLabel && experiment.rollout_durability_ended_at && (
                  <span className="text-xs text-muted-foreground">
                    {currentDurabilityOption.endedAtLabel} {formatUTCDateTime(experiment.rollout_durability_ended_at)}
                  </span>
                )}
              </header>
              <div className="space-y-4">
                {currentDurabilityOption ? (
                  <div className="rounded-md border border-border bg-muted/20 p-3">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium">{currentDurabilityOption.label}</p>
                      {/* The same decay the list shows. Without it the detail page reads
                          more confident than the row that led here. */}
                      {durabilityFreshness.isTracked && (
                        <Badge variant={getRolloutDurabilityFreshnessBadgeVariant(durabilityFreshness.level)}>
                          {durabilityFreshness.ageDays > 0
                            ? `Checked ${formatRolloutDurabilityAge(durabilityFreshness.ageDays)} ago`
                            : "Checked today"}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{currentDurabilityOption.description}</p>
                  </div>
                ) : (
                  <div className="rounded-md border border-dashed border-border p-3">
                    <p className="text-sm font-medium text-muted-foreground">Not confirmed</p>
                  </div>
                )}
                {experiment.rollout_durability_note && (
                  // Editor defaults to min-h-[220px], which leaves a large empty block
                  // under a one-line note. Read-only display should hug its content.
                  <Editor
                    value={experiment.rollout_durability_note}
                    readOnly
                    experimentId={experiment.id?.toString()}
                    editorClassName="min-h-0"
                  />
                )}
                {experiment.rollout_durability_recorded_at && (
                  <p className="text-xs text-muted-foreground">
                    Recorded {formatUTCDateTime(experiment.rollout_durability_recorded_at)}
                  </p>
                )}
                {!isEditMode && (
                  <Button
                    variant={currentDurabilityOption ? "outline" : "default"}
                    onClick={handleOpenDurabilityDialog}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    {currentDurabilityOption ? "Update Durability" : "Confirm Durability"}
                  </Button>
                )}
              </div>
            </section>
          )}
        </div>

        {/* Side rail */}
        <aside className="space-y-6 lg:sticky lg:top-6 lg:self-start lg:border-l lg:border-border lg:pl-8">
          <div>
            <div className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">Status</div>
            {currentOutcomeOption ? (
              <Badge variant={currentOutcomeOption.badgeVariant} title={currentOutcomeOption.description}>
                {currentOutcomeOption.label}
              </Badge>
            ) : (
              <Badge variant={statusVariant}>{statusLabel}</Badge>
            )}
          </div>

          {(experiment.start_date || experiment.end_date) && (
            <div>
              <div className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">Timeline</div>
              <div className="text-sm">
                {experiment.start_date ? formatUTCDate(experiment.start_date) : "—"}
                <span className="mx-1.5 text-muted-foreground">→</span>
                {experiment.end_date ? formatUTCDate(experiment.end_date) : "—"}
              </div>
            </div>
          )}

          <div>
            <div className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">Rollout</div>
            {currentRolloutOption ? (
              <Badge variant={currentRolloutOption.badgeVariant}>{currentRolloutOption.label}</Badge>
            ) : (
              <span className="text-sm text-muted-foreground">{canSetRollout ? "Not set" : "Pending"}</span>
            )}
          </div>

          {canSetDurability && (
            <div>
              <div className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">Durability</div>
              {currentDurabilityOption ? (
                <Badge variant={currentDurabilityOption.badgeVariant} title={currentDurabilityOption.description}>
                  {currentDurabilityOption.label}
                </Badge>
              ) : (
                <Badge variant="durability_unconfirmed" title="Nobody has confirmed this rollout is still live.">
                  Not confirmed
                </Badge>
              )}
            </div>
          )}

          <div className="rounded-md border border-primary/30 bg-primary/5 p-3">
            <div className="mb-2 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-primary">
              <TimerReset className="h-3.5 w-3.5" />
              <span>Learning Loop</span>
            </div>
            {learningLoopClosure.isAvailable ? (
              <div className="space-y-3">
                <div>
                  <div className="text-xs text-muted-foreground">Closure</div>
                  <div className="mt-0.5 font-mono text-2xl font-semibold tabular-nums">
                    {formatLearningLoopDuration(learningLoopClosure.closureSeconds)}
                  </div>
                </div>
                <div className="space-y-1.5 border-t border-primary/20 pt-3">
                  <SideRailMetric
                    label="End to conclusion"
                    value={formatLearningLoopDuration(learningLoopClosure.endToConclusionSeconds)}
                  />
                  <SideRailMetric
                    label="Conclusion to roll out"
                    value={formatLearningLoopDuration(learningLoopClosure.conclusionToRolloutSeconds)}
                  />
                </div>
              </div>
            ) : (
              <span className="text-sm text-muted-foreground">{learningLoopClosure.reason}</span>
            )}
          </div>
        </aside>
      </div>

      <Dialog open={isConcludeDialogOpen} onOpenChange={open => !isSaving && setIsConcludeDialogOpen(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conclude Experiment</DialogTitle>
            <DialogDescription>Select the final outcome for this experiment.</DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            {EXPERIMENT_OUTCOME_OPTIONS.map(option => (
              <button
                key={option.value}
                type="button"
                onClick={() => setSelectedOutcome(option.value)}
                className={cn(
                  "w-full rounded-md border p-3 text-left transition-colors",
                  selectedOutcome === option.value
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50",
                )}
              >
                <p className="text-sm font-medium">{option.label}</p>
                <p className="text-sm text-muted-foreground mt-1">{option.description}</p>
              </button>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsConcludeDialogOpen(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleConcludeExperiment} disabled={!selectedOutcome || isSaving}>
              {isSaving ? "Concluding..." : "Continue"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isRolloutDialogOpen} onOpenChange={handleRolloutDialogOpenChange}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Set Rollout</DialogTitle>
            <DialogDescription>Record the rollout decision, timestamp, and required reference.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <Label className="text-xs text-muted-foreground">Status</Label>
                <Select
                  value={selectedRolloutStatus}
                  onValueChange={value => setSelectedRolloutStatus(value as ExperimentRolloutStatus)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select rollout status" />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPERIMENT_ROLLOUT_OPTIONS.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Timestamp</Label>
                <div className="mt-1 flex gap-2">
                  <Popover open={rolloutPopoverOpen} onOpenChange={setRolloutPopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "flex-1 justify-start text-left font-normal",
                          !rolloutDate && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {rolloutDate ? format(rolloutDate, "LLL dd, y") : <span>Pick date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={rolloutDate || undefined}
                        onDayClick={day => {
                          setRolloutDate(day);
                          setRolloutPopoverOpen(false);
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                  <div className="relative">
                    <Input
                      type="time"
                      value={rolloutTime}
                      onChange={event => setRolloutTime(event.target.value)}
                      className="w-32 pr-12"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
                      UTC
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="rollout-additional-info">Additional info</Label>
              <textarea
                id="rollout-additional-info"
                value={rollOutAdditionalInfoDraft}
                onChange={event => setRolloutAdditionalInfoDraft(event.target.value)}
                placeholder={rollout_additional_info_PLACEHOLDER}
                className="min-h-[120px] w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => handleRolloutDialogOpenChange(false)} disabled={isSettingRollout}>
              Cancel
            </Button>
            <Button
              onClick={handleSetRollout}
              disabled={isSettingRollout || !selectedRolloutStatus || !rolloutDate || !hasRolloutAdditionalInfoDraft}
            >
              {!isSettingRollout && <CheckCircle className="h-4 w-4 mr-2" />}
              {isSettingRollout ? "Saving..." : "Set Rollout"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RolloutDurabilityDialog
        experiment={experiment}
        open={isDurabilityDialogOpen}
        onOpenChange={setIsDurabilityDialogOpen}
        onSaved={onSaveSuccess}
      />

      {isEditMode && (
        <div className="flex items-center gap-2 justify-end">
          <Button variant="outline" onClick={handleSummaryCancel} disabled={saveDisabled}>
            Cancel
          </Button>
          <Button onClick={handleSummarySave} disabled={saveDisabled}>
            {isSavingSummary ? "Saving..." : "Save"}
          </Button>
        </div>
      )}
    </div>
  );
}
